import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Volume2, VolumeX } from 'lucide-react';

interface AudioPermissionDialogProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export const AudioPermissionDialog = ({
  open,
  onAccept,
  onDecline,
}: AudioPermissionDialogProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleAccept = async () => {
    setIsProcessing(true);
    
    // Create a silent audio element and attempt to play it
    // This helps establish the user gesture for audio playback
    const audio = new Audio();
    audio.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAA4T0kJlzAAAAAAAAAAAAAAAAAAAA//sQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sQZDgP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVU=';
    audio.volume = 0.01;
    
    try {
      await audio.play();
      audio.pause();
      console.log('✅ Audio permission established via user gesture');
    } catch (error) {
      console.warn('⚠️ Audio play attempt:', error);
    }
    
    setIsProcessing(false);
    onAccept();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDecline()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Volume2 className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Enable Audio Playback</DialogTitle>
          <DialogDescription className="text-center">
            We'd like to play music previews to help you build your perfect vibe. 
            Click "Allow Audio" to enable playback.
          </DialogDescription>
        </DialogHeader>
        
        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button
            onClick={handleAccept}
            disabled={isProcessing}
            className="w-full"
            size="lg"
          >
            <Volume2 className="mr-2 h-4 w-4" />
            {isProcessing ? 'Enabling...' : 'Allow Audio'}
          </Button>
          <Button
            onClick={onDecline}
            variant="outline"
            disabled={isProcessing}
            className="w-full"
            size="lg"
          >
            <VolumeX className="mr-2 h-4 w-4" />
            Not Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
